import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper
} from "@mui/material";

// Material-UI Icons Imports
import {
  SmartToy,
  Memory,
  Campaign,
  Analytics,
  HomeRepairService,
  Plumbing,
  ElectricalServices,
  CleaningServices,
  LocalLaundryService,
  LocalShipping,
  AirportShuttle,
  Construction,
  Foundation,
  Chair,
  Weekend,
  TableRestaurant,
  FormatPaint,
  Wallpaper,
  Roofing,
  BuildCircle,
  MedicalServices,
  Computer,
  Laptop,
  Grass,
  Nature,
  Security,
  HelpOutline,
} from "@mui/icons-material";

import categoriesData from "./categories.json";
import MainCategoriesGrid from "./MainCategoriesGrid";

export interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

interface PassedProps {
  onSubmit: (categoryKeys: string[]) => void;
  isLoading: boolean;
}

const CategoriesList: React.FC<PassedProps> = ({ onSubmit, isLoading }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubcategories, setSelectedSubcategories] = useState<Category[]>([]);

  useEffect(() => {
    // Load categories from JSON on mount
    const parsedData = JSON.parse(
      JSON.stringify(categoriesData)
    ) as { categories: Category[] };
    setCategories(parsedData.categories);
  }, []);

  // -- UPDATED FILTERING FOR ROOT CATEGORIES --
  const filteredCategories = categories.filter((cat) => {
    if (!searchTerm) return true; // If no search term, show all
    const lowerTerm = searchTerm.toLowerCase();

    // Check if category name includes search term
    const catNameMatches = cat.name.toLowerCase().includes(lowerTerm);

    // Check if any subcategory name includes search term
    const subcatMatches = cat.subcategories?.some((sub) =>
      sub.name.toLowerCase().includes(lowerTerm)
    );

    return catNameMatches || subcatMatches;
  });

  // -- SUBCATEGORY FILTERING (unchanged) --
  const filteredSubcategories = selectedCategory
    ? categories
        .find((cat) => cat.key === selectedCategory.key)
        ?.subcategories.filter((sub) =>
          sub.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) ?? []
    : [];

  /** When a user clicks a main category */
  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category);
    setSearchTerm("");
    setSelectedSubcategories([]);

    // Scroll to the top for a smoother UX
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /** Check/uncheck a subcategory */
  const handleSubcategoryChange = (
    subcategory: Category,
    isChecked: boolean
  ) => {
    setSelectedSubcategories((prev) =>
      isChecked
        ? [...prev, subcategory]
        : prev.filter((item) => item !== subcategory)
    );
  };

  /** Check/uncheck all subcategories */
  const handleSelectAll = (selectAll: boolean) => {
    if (selectedCategory) {
      const foundCat = categories.find(
        (cat) => cat.key === selectedCategory.key
      );
      if (foundCat) {
        setSelectedSubcategories(selectAll ? foundCat.subcategories : []);
      }
    }
  };

  /** Final Submit -> includes the main category key + selected subcategories' keys */
  const handleSubmit = () => {
    if (!selectedCategory) return;

    const categoryKeys = selectedSubcategories.map((x) => x.key);
    categoryKeys.push(selectedCategory.key);

    onSubmit(categoryKeys);
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        py: 10,
      }}
    >
      <Box
        width={{ xs: "90%", sm: "70%", md: "50%" }}
        mx="auto"
        mt={4}
        p={{ xs: 2, sm: 4 }}
        component={Paper}
        elevation={4}
        sx={{
          borderRadius: 4,
        }}
      >
        <Typography variant="h4" textAlign="center" gutterBottom>
          Select Categories
        </Typography>
        <Typography
          textAlign="center"
          variant="body1"
          color="textSecondary"
          sx={{ mb: 3 }}
        >
          Choose the main category you plan to work in. You can add or change
          subcategories later.
        </Typography>

        {/* Search Field */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={
            selectedCategory ? "Search for a subcategory" : "Search for a category"
          }
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          sx={{ mb: 3 }}
        />

        {selectedCategory ? (
          <Box>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 3 }}>
              <Link
                component="button"
                variant="body1"
                underline="hover"
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchTerm("");
                }}
              >
                Categories
              </Link>
              <Typography color="text.primary">
                {selectedCategory.name}
              </Typography>
            </Breadcrumbs>

            {/* Subcategories */}
            <Typography variant="h5" gutterBottom>
              Subcategories for <strong>{selectedCategory.name}</strong>
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      selectedSubcategories.length ===
                      categories.find((cat) => cat.key === selectedCategory.key)
                        ?.subcategories.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label="Select All"
              />

              {filteredSubcategories.map((subcategory: Category) => (
                <FormControlLabel
                  key={subcategory.key}
                  control={
                    <Checkbox
                      checked={selectedSubcategories.includes(subcategory)}
                      onChange={(e) =>
                        handleSubcategoryChange(subcategory, e.target.checked)
                      }
                    />
                  }
                  label={subcategory.name}
                />
              ))}
            </FormGroup>

            {/* Buttons */}
            <Box mt={3} display="flex" justifyContent="space-between">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchTerm("");
                }}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{ minWidth: 100 }}
              >
                {isLoading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "white",
                    }}
                  />
                ) : (
                  "Next"
                )}
              </Button>
            </Box>
          </Box>
        ) : (
          <MainCategoriesGrid
            filteredCategories={filteredCategories}
            handleSelectCategory={handleSelectCategory}
          />
        )}
      </Box>
    </Box>
  );
};

export default CategoriesList;
